import { Alert } from "react-bootstrap";
import DefaultSlide from "../../components/Slides/templates/DefaultSlide";
import ImageSlide from "../../components/Slides/templates/ImageSlide";
import TitleSlide from "../../components/Slides/templates/TitleSlide";
import StudentPicker from "../../components/global/StudentPicker";
import { ISlideObject } from "../../components/Slides/templates/ISlideObject";
import CodeSlide from "../../components/Slides/templates/CodeSlide";
import { useEffect, useRef } from "react";
import "./S04P01.css";
import CodePen from "../../components/Codepen/Codepen";

interface ToastifyOptions {
  text: string;
  duration?: number;
  newWindow?: boolean;
  close?: boolean;
  gravity?: "top" | "bottom";
  position?: "left" | "center" | "right";
  stopOnFocus?: boolean;
  style?: Record<string, string>;
  onClick?: () => void;
}

declare function Toastify(options: ToastifyOptions): { showToast: () => void };

export default function S15P01({ reveal }: ISlideObject) {
  return (
    <>
      <TitleSlide
        title="TI8512 - Desarrollo Ágil de Aplicaciones Web"
        subtitle="Semana 15"
      />
      <DefaultSlide title="Agenda">
        <div style={{ fontSize: "0.75em" }}>
          <ul>
            <li>Docker</li>
            <li>CI/CD</li>
            <li>Github actions</li>
            <li>Cloud deployments *</li>
            <li>Presentación de proyectos</li>
          </ul>
        </div>
      </DefaultSlide>
      <section>
        <TitleSlide title="Docker" />
        <DefaultSlide title="¿Qué es Docker?">
          <div style={{ fontSize: "0.9em" }}>
            <ul>
              <li>Plataforma para desarrollar, enviar y ejecutar aplicaciones en contenedores.</li>
              <li>Permite empaquetar software con todas sus dependencias.</li>
              <li>Facilita la portabilidad y consistencia entre entornos.</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Historia de Docker">
          <div style={{ fontSize: "0.9em" }}>
            <ul>
              <li>Docker fue lanzado en 2013 por Solomon Hykes como un proyecto interno de dotCloud.</li>
              <li>Popularizó el uso de contenedores gracias a su facilidad de uso y portabilidad.</li>
              <li>Basado en tecnologías existentes como cgroups y namespaces de Linux.</li>
              <li>En 2015, Docker donó el motor de contenedores a la Cloud Native Computing Foundation (CNCF).</li>
              <li>Hoy es una herramienta fundamental en el desarrollo y despliegue de aplicaciones modernas.</li>
            </ul>
          </div>
        </DefaultSlide>
        <section>
          <img src="/images/S15/docker-apps.png" alt="Docker Architecture" />
        </section>
        <DefaultSlide title="Contenedores vs Máquinas Virtuales">
          <div style={{ fontSize: "0.85em" }}>
            <table className="table table-bordered" style={{ fontSize: "0.9em" }}>
              <thead>
                <tr>
                  <th></th>
                  <th>Contenedores</th>
                  <th>Máquinas Virtuales</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Sistema operativo</td>
                  <td>Comparten el mismo sistema operativo</td>
                  <td>Requieren un hipervisor y un SO completo</td>
                </tr>
                <tr>
                  <td>Peso</td>
                  <td>Más ligeros</td>
                  <td>Más pesados</td>
                </tr>
                <tr>
                  <td>Velocidad de inicio</td>
                  <td>Rápidos de iniciar</td>
                  <td>Más lentos de iniciar</td>
                </tr>
                <tr>
                  <td>Portabilidad</td>
                  <td>Altamente portables entre entornos</td>
                  <td>Menos portables, dependen del hipervisor</td>
                </tr>
                <tr>
                  <td>Uso</td>
                  <td>Ideal para microservicios y aplicaciones ligeras</td>
                  <td>Mejor para aplicaciones monolíticas y sistemas operativos completos</td>
                </tr>
              </tbody>
            </table>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Conceptos básicos de Docker">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li><b>Imagen:</b> Plantilla inmutable que contiene el código, dependencias y configuraciones necesarias para ejecutar una aplicación.</li>
              <li><b>Contenedor:</b> Instancia en ejecución de una imagen. Es aislado y ligero.</li>
              <li><b>Dockerfile:</b> Archivo de texto con instrucciones para construir una imagen Docker.</li>
              <li><b>Docker Hub:</b> Repositorio público para compartir y descargar imágenes Docker.</li>
              <li><b>Volumen:</b> Mecanismo para persistir datos generados y usados por contenedores.</li>
            </ul>
          </div>
        </DefaultSlide>
        <CodeSlide
          title="Estructura de un Dockerfile"
          language="dockerfile"
        >
          {`FROM node:18-alpine
WORKDIR /app
COPY package*.json ./
RUN npm install
COPY . .
EXPOSE 3000
CMD ["npm", "start"]`}
        </CodeSlide>
        <DefaultSlide title="Comandos básicos de Docker">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li><code>docker run</code>: Ejecuta un contenedor a partir de una imagen.</li>
              <li><code>docker ps</code>: Lista los contenedores en ejecución.</li>
              <li><code>docker images</code>: Muestra las imágenes disponibles localmente.</li>
              <li><code>docker build</code>: Construye una imagen a partir de un Dockerfile.</li>
              <li><code>docker pull</code>: Descarga una imagen desde un repositorio.</li>
              <li><code>docker push</code>: Sube una imagen a un repositorio.</li>
            </ul>
          </div>
        </DefaultSlide>
        <section>
          <p>¿Qué tiene que ver todo esto con CI/CD?</p>
        </section>
      </section>

      <section>
        <TitleSlide title="CI/CD" />
        <DefaultSlide title="¿Qué es CI/CD?">
          <div style={{ fontSize: "0.9em" }}>
            <ul>
              <li><b>CI (Integración Continua):</b> Práctica de integrar cambios de código frecuentemente en un repositorio compartido.</li>
              <li><b>CD (Entrega Continua):</b> Proceso de automatizar la entrega de software a producción.</li>
              <li>Ambos buscan mejorar la calidad del software y reducir el tiempo de entrega.</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Beneficios de CI/CD">
          <div style={{ fontSize: "0.9em" }}>
            <ul>
              <li>Detección temprana de errores</li>
              <li>Mejora en la colaboración del equipo</li>
              <li>Reducción del tiempo de entrega</li>
              <li>Mayor confianza en el código desplegado</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Herramientas populares de CI/CD">
          <div style={{ fontSize: "0.9em" }}>
            <ul>
              <li>Jenkins</li>
              <li>GitLab CI/CD</li>
              <li>GitHub Actions</li>
              <li>CircleCI</li>
              <li>Travis CI</li>
            </ul>
          </div>
        </DefaultSlide>
        
        <section>
          <p>No basta con simplemente decidir utilizar una herramienta, se debe crear un <b>flujo de trabajo</b> que integre la herramienta en el proceso de desarrollo.</p>
          <p>Alcanzar una cultura de desarrollo ágil involucra: personas, tecnologías y políticas.</p>
        </section>

        <CodeSlide
          title="Ejemplo de flujo de trabajo CI/CD"
          language="yaml"
        >
          {`name: CI/CD Pipeline
on:
  push:
    branches:
      - main
jobs:
  build:
    runs-on: ubuntu-latest
    steps:
      - name: Checkout code
        uses: actions/checkout@v2
      - name: Set up Node.js
        uses: actions/setup-node@v2
        with:
          node-version: '18'
      - name: Install dependencies
        run: npm install
      - name: Run tests
        run: npm test
      - name: Build application
        run: npm run build
      - name: Deploy to production
        run: npm run deploy`}
        </CodeSlide>

        <DefaultSlide title="Despliegue en la nube">
          <div style={{ fontSize: "0.9em" }}>
            <ul>
              <li>Permite escalar aplicaciones fácilmente.</li>
              <li>Proveedores populares: AWS, Azure, Google Cloud, Heroku.</li>
              <li>Integración con CI/CD para despliegues automáticos.</li>
            </ul>
          </div>
        </DefaultSlide>
      </section>

      <ImageSlide image="/images/S06/dancing6.gif" title="Fin de la clase" />
    </>
  );
}
